import { type FC, type FormEvent, useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import { Link } from "react-router";
import {
  ORDER_CHANGE_REQUEST_MAX,
  ORDER_CHANGE_REQUEST_MIN,
} from "../../../libs/mypage/orderChangeLimits";
import { normalizeOrderCode } from "../../../libs/mypage/orderCode";
import { Button } from "../components/Button";
import { Container } from "../components/Container";
import { FieldTextarea } from "../components/FieldTextarea";
import { formatDate, formatYen } from "../components/format";
import { LinkButton } from "../components/LinkButton";
import { PageHeading } from "../components/PageHeading";
import { SectionHeading } from "../components/SectionHeading";
import { StatusHeader } from "../components/StatusHeader";
import { useDecodedOrderCode } from "../hooks/useDecodedOrderCode";
import { useDirtyGuard } from "../hooks/useDirtyGuard";
import { useOrder } from "../hooks/useOrder";
import { apiClient } from "../rpc";

const meOrderChange = apiClient.me.orders[":code"]["change-request"];

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "done" }
  | { status: "error"; message: string };

export const OrderChangeFormPage: FC = () => {
  const code = useDecodedOrderCode();
  const result = useOrder(code);

  if (result.status === "loading") {
    return (
      <Container>
        <p className="py-8 text-center text-neutral-500 text-sm">読み込み中…</p>
      </Container>
    );
  }
  if (result.status === "not-found") {
    return (
      <Container>
        <PageHeading title="ご注文が見つかりませんでした" />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }
  if (result.status === "error") {
    return (
      <Container>
        <PageHeading title="注文情報を取得できませんでした" description={result.message} />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }

  const order = result.order;
  if (!order.isOrderChangeable) {
    return (
      <Container>
        <p className="mb-4 text-neutral-500 text-xs">注文番号 {order.code}</p>
        <PageHeading
          title="この注文は内容を変更できません"
          description="出荷作業に入っている、または既にキャンセル/出荷済みのご注文です。詳細はサポートまでお問い合わせください。"
        />
        <Link
          to={`/orders/${normalizeOrderCode(order.code)}`}
          className="text-sm underline underline-offset-4"
        >
          注文詳細に戻る
        </Link>
      </Container>
    );
  }

  return (
    <OrderChangeFormBody
      code={order.code}
      createdAt={order.createdAt}
      totalPriceJpy={order.totalPriceJpy}
      isCancelable={order.isCancelable}
    />
  );
};

export const OrderChangeFormBody: FC<{
  code: string;
  createdAt: string;
  totalPriceJpy: number;
  /** キャンセルできる注文なら、フォームの下にキャンセルの案内への導線を出す */
  isCancelable: boolean;
}> = ({ code, createdAt, totalPriceJpy, isCancelable }) => {
  const [request, setRequest] = useState("");
  const [state, setState] = useState<SubmitState>({ status: "idle" });

  const isDirty = request.length > 0 && state.status !== "submitting" && state.status !== "done";
  useDirtyGuard(isDirty);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = request.trim();
    if (trimmed.length < ORDER_CHANGE_REQUEST_MIN) {
      setState({
        status: "error",
        message: `変更したい内容は ${ORDER_CHANGE_REQUEST_MIN} 文字以上入力してください`,
      });
      return;
    }
    if (trimmed.length > ORDER_CHANGE_REQUEST_MAX) {
      setState({
        status: "error",
        message: `変更したい内容は ${ORDER_CHANGE_REQUEST_MAX} 文字以内で入力してください`,
      });
      return;
    }

    setState({ status: "submitting" });
    try {
      const slug = normalizeOrderCode(code);
      const res = await meOrderChange.$post({ param: { code: slug }, json: { request: trimmed } });
      if (res.status === 200) {
        setState({ status: "done" });
        return;
      }
      const data = await res.json().catch(() => ({}));
      const message =
        "message" in data && typeof data.message === "string"
          ? data.message
          : `ご依頼を送信できませんでした (HTTP ${res.status})`;
      setState({ status: "error", message });
    } catch (e) {
      setState({
        status: "error",
        message: e instanceof Error ? e.message : "ご依頼を送信できませんでした",
      });
    }
  };

  if (state.status === "done") {
    return <OrderChangeDoneView code={code} />;
  }

  const submitting = state.status === "submitting";
  const length = request.trim().length;

  return (
    <Container>
      <Link
        to={`/orders/${normalizeOrderCode(code)}`}
        className="mb-6 inline-block text-neutral-500 text-xs hover:text-neutral-900"
      >
        ← 注文詳細
      </Link>
      <PageHeading
        title="注文内容の変更"
        description="商品・色・サイズ・数量などの変更をご依頼いただけます。弊社の担当者が内容を確認して対応いたします。"
      />

      <SectionHeading title="対象の注文" />
      <dl className="mb-8 divide-y divide-neutral-200 text-sm">
        <div className="flex items-baseline justify-between gap-3 py-3">
          <dt className="text-neutral-500">注文番号</dt>
          <dd className="text-neutral-900">{code}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-3">
          <dt className="text-neutral-500">注文日</dt>
          <dd className="text-neutral-900">{formatDate(createdAt)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 py-3">
          <dt className="text-neutral-500">合計</dt>
          <dd className="font-medium text-neutral-900">{formatYen(totalPriceJpy)}</dd>
        </div>
      </dl>

      <SectionHeading title="ご依頼のあとの流れ" />
      <ol className="mb-8 list-decimal space-y-2 py-4 pl-5 text-neutral-700 text-sm leading-relaxed">
        <li>弊社の担当者がご依頼の内容を確認するまで、ご注文の発送を一時的に保留いたします。</li>
        <li>弊社の担当者が内容を確認し、あらためてメールでご連絡いたします。</li>
        <li>
          変更の内容によっては、差額の返金や追加のお支払いをお願いする場合がございます。その場合も、メールでご案内いたします。
        </li>
      </ol>
      <p className="-mt-4 mb-8 text-neutral-500 text-xs leading-relaxed">
        ご依頼と入れ違いで発送の準備に入っていた場合は、変更をお受けできないことがございます。
      </p>

      <p className="mb-6 text-neutral-700 text-sm leading-relaxed">
        こちらでは、ご注文のキャンセルのご依頼はお受けできません。
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <FieldTextarea
          label="変更したい内容 (必須)"
          name="request"
          required
          minLength={ORDER_CHANGE_REQUEST_MIN}
          maxLength={ORDER_CHANGE_REQUEST_MAX}
          value={request}
          onChange={(e) => setRequest(e.currentTarget.value)}
          rows={6}
          placeholder="変更したい商品・色・サイズ・数量などを、変更前と変更後が分かるようにご記入ください"
          hint={`${ORDER_CHANGE_REQUEST_MIN} 文字以上 ${ORDER_CHANGE_REQUEST_MAX} 文字以内でご入力ください`}
          counterMax={ORDER_CHANGE_REQUEST_MAX}
          error={state.status === "error" ? state.message : undefined}
        />

        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
          <LinkButton to={`/orders/${normalizeOrderCode(code)}`} variant="outline">
            戻る
          </LinkButton>
          <Button type="submit" loading={submitting} disabled={length < ORDER_CHANGE_REQUEST_MIN}>
            {submitting ? "送信中…" : "変更を依頼する"}
          </Button>
        </div>
      </form>

      {isCancelable && (
        <div className="mt-12 border-neutral-200 border-t pt-6 text-neutral-600 text-sm leading-relaxed">
          <p className="mb-1 font-medium text-neutral-900">ご注文のキャンセルをご希望の場合</p>
          <p>
            ご注文そのものを取りやめたい場合は、
            <Link
              to={`/orders/${normalizeOrderCode(code)}/cancel`}
              className="underline underline-offset-4 hover:text-neutral-900"
            >
              キャンセルのご案内
            </Link>
            をご確認ください。
          </p>
        </div>
      )}
    </Container>
  );
};

export const OrderChangeDoneView: FC<{ code: string }> = ({ code }) => (
  <Container>
    <StatusHeader
      tone="success"
      icon={<FiCheckCircle size={30} />}
      title="注文内容の変更のご依頼を受け付けました"
    >
      <p>
        ご注文 {code}{" "}
        の変更のご依頼を受け付けました。確認のメールをお送りしましたのでご確認ください。
      </p>
    </StatusHeader>

    <div className="space-y-3 py-4 text-neutral-700 text-sm leading-relaxed">
      <p>
        弊社の担当者がご依頼の内容を確認するまで、ご注文の発送を一時的に保留しております。確認のうえ、あらためてメールでご連絡いたします。
      </p>
    </div>

    <div className="mt-12 border-neutral-200 border-t pt-6">
      <Link
        to={`/orders/${normalizeOrderCode(code)}`}
        className="text-sm underline underline-offset-4 hover:text-neutral-900"
      >
        注文詳細に戻る
      </Link>
    </div>
  </Container>
);
